import React from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { resolveFaIcon } from 'helpers/faIcons';
import CgRadixButton from './CgRadixButton';
import { useTranslation } from 'react-i18next';

/**
 * CgEmptyState — the single, doodle-free empty-state primitive.
 *
 * Three intents, chosen by the caller (or by the host table, which flips a
 * first-run caller to `no-results` automatically when filters are active):
 *
 *  - `first-run`     What a page will show plus a primary create CTA and an
 *                    optional secondary import CTA. The generalised
 *                    `emptyStateVariant="dataEntry"` behaviour. Also serves
 *                    single-action variants (report / navigate) via `onAction`.
 *  - `no-results`    Shown only when a search/filter is active — "No matches
 *                    for the current filters" + a Clear-filters affordance.
 *                    Never fabricates search copy on a page with no search.
 *  - `empty-section` Compact, muted line for card / detail-tab contexts, with
 *                    an optional small CTA.
 *
 * Retires the sad-face + curved-arrow doodles entirely. Colours use the brand
 * teal (`primaryColor`) accent over slate neutrals, and every CTA is a
 * {@link CgRadixButton} so empty states match the Radix/Tailwind chrome of the
 * tables and dialogs they sit inside (not the Bootstrap-era CgButton).
 *
 * @param {Object} props
 * @param {'first-run'|'no-results'|'empty-section'} [props.intent='empty-section']
 * @param {string} [props.icon] FontAwesome icon name; each intent has a default.
 * @param {string} [props.title]
 * @param {string} [props.description]
 * @param {Function} [props.onCreate] first-run primary (create) handler.
 * @param {string} [props.createLabel='Create manually']
 * @param {Function} [props.onImport] first-run secondary (import) handler.
 * @param {string} [props.importLabel='Import via Excel']
 * @param {Function} [props.onAction] first-run single-action handler (report / navigate).
 * @param {string} [props.actionLabel]
 * @param {string} [props.actionIcon='faArrowRight']
 * @param {Function} [props.onClearFilters] no-results clear-filters handler.
 * @param {string} [props.clearFiltersLabel='Clear filters']
 * @param {string} [props.className]
 */
const CgEmptyState = ({
  intent = 'empty-section',
  icon,
  title,
  description,
  onCreate,
  createLabel,
  onImport,
  importLabel,
  onAction,
  actionLabel,
  actionIcon = 'faArrowRight',
  onClearFilters,
  clearFiltersLabel,
  className = '',
}) => {
  const { t } = useTranslation('common');
  const createText = createLabel ?? t('emptyState.createManually', 'Create manually');
  const importText = importLabel ?? t('emptyState.importViaExcel', 'Import via Excel');
  const clearFiltersText = clearFiltersLabel ?? t('emptyState.clearFilters', 'Clear filters');
  const noMatchesTitle = t('emptyState.noMatches', 'No matches for the current filters');
  const nothingYetTitle = t('emptyState.nothingYet', 'Nothing here yet');
  const renderIcon = (name, wrapperClass, glyphClass) => {
    const resolved = resolveFaIcon(name);
    if (!resolved) return null;
    return (
      <div className={wrapperClass}>
        <FontAwesomeIcon
          icon={resolved}
          className={glyphClass}
        />
      </div>
    );
  };

  if (intent === 'no-results') {
    return (
      <div
        className={`flex flex-col items-center justify-center text-center px-6 py-12 ${className}`}
      >
        {renderIcon(
          icon || 'faMagnifyingGlass',
          'mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-slate-400',
          'text-lg',
        )}
        <p className="text-sm font-semibold text-slate-700">{title || noMatchesTitle}</p>
        {description && <p className="mt-1 max-w-md text-sm text-slate-500">{description}</p>}
        {onClearFilters && (
          <CgRadixButton
            className="mt-4"
            size="sm"
            variant="outline"
            onClick={onClearFilters}
          >
            {clearFiltersText}
          </CgRadixButton>
        )}
      </div>
    );
  }

  if (intent === 'first-run') {
    const hasCreateImport = Boolean(onCreate || onImport);
    return (
      <div
        className={`flex flex-col items-center justify-center text-center px-6 py-14 ${className}`}
      >
        {renderIcon(
          icon || 'faInbox',
          'mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primaryColor/10 text-primaryColor',
          'text-2xl',
        )}
        <p className="mb-1 text-base font-semibold text-slate-800">{title || nothingYetTitle}</p>
        {description && <p className="mb-6 max-w-md text-sm text-slate-500">{description}</p>}
        {(hasCreateImport || onAction) && (
          <div className="flex flex-col items-center gap-3 sm:flex-row">
            {onCreate && (
              <CgRadixButton
                variant="primary"
                size="lg"
                icon={resolveFaIcon('faPlusCircle')}
                onClick={onCreate}
              >
                {createText}
              </CgRadixButton>
            )}
            {onImport && (
              <CgRadixButton
                variant="outline"
                size="lg"
                icon={resolveFaIcon('faFileExcel')}
                onClick={onImport}
              >
                {importText}
              </CgRadixButton>
            )}
            {!hasCreateImport && onAction && (
              <CgRadixButton
                variant="primary"
                size="lg"
                icon={resolveFaIcon(actionIcon)}
                onClick={onAction}
              >
                {actionLabel}
              </CgRadixButton>
            )}
          </div>
        )}
      </div>
    );
  }

  // empty-section — compact, muted, for card / detail-tab contexts.
  return (
    <div
      className={`flex flex-col items-center justify-center text-center px-6 py-10 ${className}`}
    >
      {renderIcon(icon || 'faInbox', 'mb-2 text-slate-300', 'text-2xl')}
      <p className="text-sm font-medium text-slate-500">{title || nothingYetTitle}</p>
      {description && <p className="mt-1 max-w-sm text-xs text-slate-400">{description}</p>}
      {onAction && (
        <CgRadixButton
          className="mt-4"
          size="sm"
          variant="outline"
          icon={resolveFaIcon(actionIcon)}
          onClick={onAction}
        >
          {actionLabel}
        </CgRadixButton>
      )}
    </div>
  );
};

CgEmptyState.propTypes = {
  intent: PropTypes.oneOf(['first-run', 'no-results', 'empty-section']),
  icon: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  onCreate: PropTypes.func,
  createLabel: PropTypes.string,
  onImport: PropTypes.func,
  importLabel: PropTypes.string,
  onAction: PropTypes.func,
  actionLabel: PropTypes.string,
  actionIcon: PropTypes.string,
  onClearFilters: PropTypes.func,
  clearFiltersLabel: PropTypes.string,
  className: PropTypes.string,
};

export default CgEmptyState;
